import React, { ReactNode, useState } from 'react';
import { ProductActionsProvider, ProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { AlgoliaProductHit, AlgoliaProductHitFamilyVariant } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { updateCart, getCartLoading, setCartLoading, transformBonusItemsForCart } from '../utils/cart/cartUtils';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { getStoreCurrency } from '../utils/shopify/getStoreCurrency';
import { AttachAddToCartSlideout } from '../templates/components/attachAddToCartSlideout/AttachAddToCartSlideout';
import {
  AlgoliaHitTransformer,
  getSkuForVariant,
} from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import {
  postAlgoliaAddToCart,
  postAlgoliaProductClick,
  postAlgoliaFilterConvert,
} from '../utils/algolia/search-insights';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { getMarketplacePropertiesBySeller } from '../utils/cart/refreshTieredProductDetails';
import { ShopifyStorefrontClient } from '../api/shopify/ShopifyStorefrontClient';
import { hasReachedMaxQty } from '../utils/checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../utils/checkMaxQty/showMaxQtyAlert';
import { TrackingDescriptorType } from '@jbhi-fi/components/src/Tracking/types';
import { klaviyoAddedToCart } from '../utils/klaviyo';
import { useCartContext } from '../utils/cart/context/CartContext';
import { useProductClientContext } from '@jbhi-fi/components/src/context/ProductClientProvider';
import { trackViewCartShopify } from '../utils/cart/trackViewCartShopify';

const storefrontClient = new ShopifyStorefrontClient();

const handleAddToCart = async (
  hit: AlgoliaProductHit,
  variantId?: number,
  trackingDescriptor?: TrackingDescriptorType,
  bonusProducts?: Array<AlgoliaProductHit>
) => {
  const variant = hit.children?.find((child: AlgoliaProductHitFamilyVariant) => child.variant_id === variantId);
  const variantID = variant?.variant_id ?? hit.variant_id;
  const handle = variant?.handle ?? hit.handle;
  const shouldUseCustomTracking = !!trackingDescriptor;
  const { list, itemListSlot, itemListServiceConfig } = trackingDescriptor?.ecommerceExtraProps ?? {};
  const listName = list ?? trackingDescriptor?.group;

  if (hit.button === 'CheckAvailability') {
    window.localStorage.setItem('openAvailabilityModal', 'true');
    window.location.href = `/products/${handle}?variant=${variantID}`;
    return;
  }
  if (hit.button === 'NotifyMe') {
    window.location.href = `/products/${handle}?variant=${variantID}&notifyClicked=true`;
    return;
  }
  if (hit.button === 'View') {
    window.location.href = `/products/${handle}?variant=${variantID}`;
    return;
  }

  if (getCartLoading()) {
    return;
  }
  if (hasReachedMaxQty(variantID)) {
    showMaxQtyAlert();
    return;
  }
  setCartLoading(true);
  const marketplaceSeller = AlgoliaHitTransformer.toMarketplaceSeller(hit);
  const marketplaceProperties = getMarketplacePropertiesBySeller(marketplaceSeller);
  const properties = {
    _availabilityCanPreOrder: hit.availability?.canPreOrder,
    _availabilityFulfilment: hit.availability?.fulfilment,
    _maxQty: hit.availability.fulfilment === 'Digital' ? 1 : hit.product?.limitPerOrder,
    _priceAtc: Math.round(hit.price * 100),
    _itemListName: listName,
    _itemListSlot: itemListSlot,
    _itemListServiceConfig: itemListServiceConfig,
    _productGroup: hit.productGroup?.id,
    _eligibleForServices: hit.product?.eligibleForServices ? 'true' : 'false',
  };

  const attributes = filterAndMapAttributes({ ...properties, ...marketplaceProperties });
  const primaryItem = { variantId: variantID, quantity: 1, attributes };
  const bonusLineItems = transformBonusItemsForCart(bonusProducts, trackingDescriptor?.ecommerceExtraProps?.list);
  const cart = await storefrontClient.addMultipleToCart([primaryItem, ...bonusLineItems]);
  if (shouldUseCustomTracking && trackingDescriptor) {
    trackAddToCart({
      location: trackingDescriptor.page,
      currency: getStoreCurrency(),
      addedItems: transformAlgoliaHitForTracking(hit, {
        listName,
        listSlot: itemListSlot,
        listServiceConfig: itemListServiceConfig,
      }),
    });
    if (trackingDescriptor.page === 'cart') {
      trackViewCartShopify(cart, 'cart');
    }
  }
  klaviyoAddedToCart(cart);
  postAlgoliaFilterConvert();
  postAlgoliaAddToCart(getSkuForVariant(hit, variantID), hit.__queryID);
  return cart;
};

const handleCardClick = (sku: string, position?: number, queryId?: string) => {
  postAlgoliaProductClick(sku, position, queryId);
};

export interface ShopifyProductActionsProviderProps {
  children: ReactNode;
  attachPageType?: keyof typeof window.Jbhifi.attachConfig.enabledPages;
}

const hideAttachSlideoutSources = ['alternatives'];

export const ShopifyProductActionsProvider = ({ children, attachPageType }: ShopifyProductActionsProviderProps) => {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [purchasedProduct, setPurchasedProduct] = useState<AlgoliaProductHit | null>(null);
  const [bonusProducts, setBonusProducts] = useState<Array<AlgoliaProductHit>>([]);
  const { toggleMiniCart, location } = useCartContext();
  const isAttachSlideoutEnabled = attachPageType && window.Jbhifi?.attachConfig?.enabledPages?.[attachPageType];
  const hiddenPreambleProductGroupIds = window.Jbhifi?.hiddenProductCardSubtextProductGroupIds
    ?.split(',')
    .map(s => s.trim());
  const productClient = useProductClientContext();

  const onAddToCart: ProductActionsContext['onAddToCart'] = async (
    event,
    hit,
    variantId,
    trackingDescriptor,
    source
  ) => {
    const isMarketplaceProduct = AlgoliaHitTransformer.isMarketplaceProduct(hit);
    const shouldShowSlideout =
      isAttachSlideoutEnabled &&
      !hideAttachSlideoutSources.includes(source || '') &&
      !isMarketplaceProduct &&
      location !== 'cart';
    const freeBonusSkus = hit.product?.freeBonusSkus;
    const bonusProducts =
      (freeBonusSkus?.length && (await productClient.getProductsFromSkus(freeBonusSkus)).data?.products) || [];
    const updatedCart = await handleAddToCart(hit, variantId, trackingDescriptor, bonusProducts);
    if (updatedCart) {
      if (shouldShowSlideout) {
        setPurchasedProduct(hit);
        setBonusProducts(bonusProducts);
        toggleMiniCart(false);
        setIsSlideoutOpen(true);
      }
      await updateCart(updatedCart);
    }
    return !!updatedCart;
  };

  const handleSlideoutClose = () => {
    setIsSlideoutOpen(false);
    setPurchasedProduct(null);
  };

  return (
    <>
      {purchasedProduct ? (
        <AttachAddToCartSlideout
          isOpen={isSlideoutOpen}
          onClose={handleSlideoutClose}
          pageType={attachPageType === 'collections' ? 'collection' : attachPageType ?? ''}
          purchasedProduct={purchasedProduct}
          freeBonusProducts={bonusProducts}
          shouldFetchServiceSuggestions={purchasedProduct?.product?.eligibleForServices}
        />
      ) : null}
      <ProductActionsProvider
        onAddToCart={onAddToCart}
        productCartPropsOverrides={{
          shouldHideCartOverlay: isAttachSlideoutEnabled,
          hiddenPreambleProductGroupIds,
          onClick: handleCardClick,
        }}
      >
        {children}
      </ProductActionsProvider>
    </>
  );
};
